import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { KeyRound, ShieldCheck } from "lucide-react";

// Carte de gestion 2FA (TOTP) pour les utilisateurs internes.
export function TwoFactorCard() {
  const [enabled, setEnabled] = useState(null);
  const [setup, setSetup] = useState(null); // {qr, secret}
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadStatus = () => api.get("/platform/2fa/status").then(({ data }) => setEnabled(data.enabled)).catch(() => {});
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/platform/2fa/setup"); setSetup(data); setRecovery([]); }
    catch { toast.error("Erreur"); } finally { setBusy(false); }
  };
  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/platform/2fa/enable", { code });
      setRecovery(data.recovery_codes); setSetup(null); setCode(""); setEnabled(true);
      toast.success("2FA activée");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Code invalide"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    const c = window.prompt("Entrez un code 2FA pour désactiver :");
    if (!c) return;
    try { await api.post("/platform/2fa/disable", { code: c }); setEnabled(false); toast.success("2FA désactivée"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Code invalide"); }
  };

  const inputCls = "w-full rounded-lg border border-white/15 bg-black/40 px-3 py-2 text-sm text-white focus:border-[#EAB308] focus:outline-none";

  return (
    <div className="rounded-xl border border-white/10 bg-[#111217] p-5" data-testid="twofa-card">
      <h2 className="mb-1 flex items-center gap-2 text-lg font-bold text-white"><ShieldCheck className="w-5 h-5 text-[#EAB308]" /> Authentification à deux facteurs</h2>
      <p className="mb-4 text-xs text-white/45">Sécurise votre accès interne avec une application d'authentification (TOTP).</p>

      {enabled === true && !setup && (
        <div className="flex items-center justify-between rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-4 py-3">
          <span className="text-sm text-emerald-300">✓ 2FA activée</span>
          <button onClick={disable} data-testid="twofa-disable" className="rounded-md border border-white/15 px-3 py-1 text-xs text-white/70 hover:text-white">Désactiver</button>
        </div>
      )}

      {enabled === false && !setup && (
        <button onClick={startSetup} disabled={busy} data-testid="twofa-setup" className="flex items-center gap-2 rounded-lg bg-[#EAB308] px-4 py-2 text-sm font-bold text-black hover:bg-[#ffd45c]">
          <KeyRound className="w-4 h-4" /> Activer la 2FA
        </button>
      )}

      {setup && (
        <div className="space-y-3">
          <p className="text-xs text-white/60">Scannez ce QR avec Google Authenticator / Authy, puis entrez le code.</p>
          <img src={setup.qr} alt="QR 2FA" className="h-40 w-40 rounded-lg bg-white p-2" data-testid="twofa-qr" />
          <div className="text-xs text-white/40 font-mono break-all">Clé : {setup.secret}</div>
          <input className={inputCls} placeholder="Code à 6 chiffres" value={code} onChange={(e) => setCode(e.target.value)} data-testid="twofa-code" />
          <button onClick={enable} disabled={busy} data-testid="twofa-enable" className="w-full rounded-lg bg-[#EAB308] px-4 py-2 text-sm font-bold text-black hover:bg-[#ffd45c] disabled:opacity-50">Confirmer & activer</button>
        </div>
      )}

      {recovery.length > 0 && (
        <div className="mt-4 rounded-lg border border-[#EAB308]/30 bg-[#EAB308]/5 p-4" data-testid="twofa-recovery">
          <div className="mb-2 text-xs font-bold text-[#EAB308]">Codes de récupération (à conserver)</div>
          <div className="grid grid-cols-2 gap-1 font-mono text-sm text-white">
            {recovery.map((c) => <span key={c}>{c}</span>)}
          </div>
        </div>
      )}
    </div>
  );
}
